/**
 * 批量加载CDN资源
 * 
 * 该函数会并行加载多个CDN资源，支持JavaScript文件和CSS文件。
 * 所有资源加载完成后返回Promise，如果任何资源加载失败则抛出异常。
 * 
 * @param resources - 要加载的资源配置对象
 * @param options - 加载选项
 * @param options.timeout - 超时时间（毫秒），默认30000ms
 * @param options.retryCount - 重试次数，默认3次
 * @param options.retryDelay - 重试延迟（毫秒），默认1000ms
 * @returns Promise<void> 所有资源加载完成
 * @throws {Error} 当资源加载失败时抛出
 * 
 * @example
 * ```ts
 * import { loadAllNeedCDNs } from '@fu/matrix';
 * 
 * const resources = {
 *   F10Utils: '//s.thsi.cn/cb?cd/website-thsc-f10-utils/1.4.37/thsc-f10-utils.js',
 *   API: '//s.thsi.cn/cb?cd/b2cweb-fe-common-resource-pod/js/cefApi/1.5.6/cefApi.min.js',
 * };
 * 
 * try {
 *   await loadAllNeedCDNs(resources);
 *   console.log('所有资源加载完成');
 * } catch (error) {
 *   console.error('资源加载失败:', error);
 * }
 * 
 * // 自定义配置
 * await loadAllNeedCDNs(resources, {
 *   timeout: 15000,
 *   retryCount: 2,
 *   retryDelay: 500
 * });
 * ```
 * 
 * @since 1.0.0
 */

// 资源类型枚举
type ResourceType = 'script' | 'css' | 'unknown';

// 加载选项接口
interface LoadOptions {
  timeout?: number;
  retryCount?: number;
  retryDelay?: number;
}

// 资源加载错误类
class ResourceLoadError extends Error {
  constructor(
    message: string,
    public resourceName: string,
    public url: string,
    public type: 'timeout' | 'load' | 'type'
  ) {
    super(message);
    this.name = 'ResourceLoadError';
  }
}

/**
 * 检查当前环境是否支持资源加载
 */
function checkEnvironment(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('loadAllNeedCDNs 仅支持浏览器环境');
  }
}

/**
 * 验证输入参数
 */
function validateParameters(
  resources: Record<string, string>,
  options?: LoadOptions
): void {
  if (!resources || typeof resources !== 'object') {
    throw new TypeError('resources 参数必须是对象');
  }

  if (Object.keys(resources).length === 0) {
    throw new TypeError('resources 对象不能为空');
  }

  const { timeout = 30000, retryCount = 3, retryDelay = 1000 } = options || {};

  if (timeout <= 0) {
    throw new TypeError('timeout 必须大于0');
  }

  if (retryCount < 0) {
    throw new TypeError('retryCount 不能为负数');
  }

  if (retryDelay < 0) {
    throw new TypeError('retryDelay 不能为负数');
  }
}

/**
 * 检测资源类型
 */
function getResourceType(url: string): ResourceType {
  const urlLower = url.toLowerCase();
  
  // 更严格的检测逻辑
  if (urlLower.endsWith('.js') || urlLower.includes('.js?')) {
    return 'script';
  }
  
  if (urlLower.endsWith('.css') || urlLower.includes('.css?')) {
    return 'css';
  }
  
  return 'unknown';
}

/**
 * 创建脚本元素
 */
function createScriptElement(url: string): HTMLScriptElement {
  const script = document.createElement('script');
  script.src = url;
  script.async = true;
  return script;
}

/**
 * 创建样式链接元素
 */
function createStyleElement(url: string): HTMLLinkElement {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  return link;
}

/**
 * 加载单个资源
 */
async function loadResource(
  name: string,
  url: string,
  options: Required<LoadOptions>
): Promise<void> {
  const { timeout, retryCount, retryDelay } = options;
  const resourceType = getResourceType(url);

  if (resourceType === 'unknown') {
    throw new ResourceLoadError(
      `不支持的资源类型: ${url}`,
      name,
      url,
      'type'
    );
  }

  return new Promise<void>((resolve, reject) => {
    let attempts = 0;
    let timeoutId: number;

    const attemptLoad = () => {
      attempts++;
      
      // 设置超时
      timeoutId = window.setTimeout(() => {
        if (attempts <= retryCount) {
          console.warn(`资源加载超时，正在重试 (${attempts}/${retryCount}): ${name}`);
          setTimeout(attemptLoad, retryDelay);
        } else {
          reject(new ResourceLoadError(
            `资源加载超时: ${name} (${url})`,
            name,
            url,
            'timeout'
          ));
        }
      }, timeout);

      const element = resourceType === 'script' 
        ? createScriptElement(url)
        : createStyleElement(url);

      const onLoad = () => {
        clearTimeout(timeoutId);
        resolve();
      };

      const onError = () => {
        clearTimeout(timeoutId);
        if (attempts <= retryCount) {
          console.warn(`${resourceType === 'script' ? '脚本' : '样式'}加载失败，正在重试 (${attempts}/${retryCount}): ${name}`);
          setTimeout(attemptLoad, retryDelay);
        } else {
          reject(new ResourceLoadError(
            `${resourceType === 'script' ? '脚本' : '样式'}加载失败: ${name} (${url})`,
            name,
            url,
            'load'
          ));
        }
      };

      element.onload = onLoad;
      element.onerror = onError;
      
      document.head.appendChild(element);
    };

    attemptLoad();
  });
}

export function loadAllNeedCDNs(
  resources: Record<string, string>,
  options?: LoadOptions
): Promise<void> {
  // 环境检查
  checkEnvironment();

  // 参数验证
  validateParameters(resources, options);

  // 设置默认选项
  const {
    timeout = 30000,
    retryCount = 3,
    retryDelay = 1000
  } = options || {};

  // 并行加载所有资源
  const loadPromises = Object.entries(resources).map(([name, url]) => 
    loadResource(name, url, { timeout, retryCount, retryDelay })
  );

  return Promise.all(loadPromises).then(() => {
    // 所有资源加载完成
  });
} 